import { useTranslation } from "react-i18next";
import { RootState, useSelector } from "src/store";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { selectGroupById, selectGroups } from "@groups/store/selectors";
import { PermissionService } from "@services/permissionService/permissionService";

type GroupDeleteGate = {
    isDeleteDisabled: boolean;
    /**
     * Empty while the group is deletable, so the caller can fall back to its own idle tooltip.
     */
    blockedReason: string;
};

/**
 * Single source of the "may this group be deleted?" rule, shared by the groups table row actions
 * and the group side panel menu. Both used to carry their own copy and drifted apart in wording.
 *
 * Deleting the default enrollment group is allowed: the backend hands that role over to another
 * group. It only refuses when no group would be left, hence the last one stays undeletable.
 */
export function useGroupDeleteGate(groupId: number): GroupDeleteGate {
    const { t } = useTranslation(["common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();

    const groups = useSelector(selectGroups);
    const group = useSelector((state: RootState) => selectGroupById(state, groupId));

    let blockedReason = "";
    if (groupAbility(group || null, workspace).cannot("delete", "Group")) {
        blockedReason = t("common:tooltip.noPermissionToDeleteGroup");
    } else if (groups.some((item) => item.parentId === groupId)) {
        blockedReason = t("common:tooltip.groupWithNestedGroups");
    } else if (groups.length === 1) {
        blockedReason = t("common:tooltip.cannotDeleteLastGroup");
    }

    return { isDeleteDisabled: Boolean(blockedReason), blockedReason };
}
